import { useEffect, useState } from "react";
import {
  type AdminDeployServer,
  type DeployServerInput,
  createDeployServer,
  deleteDeployServer,
  listDeployServersAdmin,
  updateDeployServer,
} from "../api/deploy";
import { IconBack, IconPlus, IconTrash, IconX } from "./admin/icons";
import "./admin/members.css";
import "./deploy-settings.css";

type Props = { onClose: () => void };

type FormState = {
  id: string;
  label: string;
  host: string;
  port: string;
  username: string;
  password: string;
  remoteDir: string;
  protocol: "sftp" | "ftp" | "ftps";
  allowInsecure: boolean;
  hostKeySha256: string;
};

const EMPTY: FormState = {
  id: "",
  label: "",
  host: "",
  port: "2022",
  username: "",
  password: "",
  remoteDir: "plugins",
  protocol: "sftp",
  allowInsecure: false,
  hostKeySha256: "",
};

function toForm(s: AdminDeployServer): FormState {
  return {
    id: s.id,
    label: s.label,
    host: s.host,
    port: String(s.port),
    username: s.username,
    password: "",
    remoteDir: s.remoteDir,
    protocol: s.protocol,
    allowInsecure: s.allowInsecure,
    hostKeySha256: s.hostKeySha256 ?? "",
  };
}

export default function DeploySettingsPage({ onClose }: Props) {
  const [servers, setServers] = useState<AdminDeployServer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // null = no form open; "new" = create; otherwise editing that server id.
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setError(null);
    try {
      setServers(await listDeployServersAdmin());
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => { void refresh(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (s: AdminDeployServer) => { setForm(toForm(s)); setEditing(s.id); };
  const closeForm = () => { setEditing(null); setForm(EMPTY); };

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    const port = Number.parseInt(form.port, 10);
    if (!Number.isInteger(port) || port <= 0) {
      setError("Port must be a positive integer.");
      setSaving(false);
      return;
    }
    try {
      if (editing === "new") {
        if (!form.password) throw new Error("Password is required for a new server.");
        const input: DeployServerInput = {
          id: form.id.trim(),
          label: form.label.trim(),
          host: form.host.trim(),
          port,
          username: form.username.trim(),
          password: form.password,
          remoteDir: form.remoteDir.trim(),
          protocol: form.protocol,
          allowInsecure: form.allowInsecure,
          ...(form.hostKeySha256.trim() ? { hostKeySha256: form.hostKeySha256.trim() } : {}),
        };
        await createDeployServer(input);
      } else if (editing) {
        const patch: Partial<Omit<DeployServerInput, "id">> = {
          label: form.label.trim(),
          host: form.host.trim(),
          port,
          username: form.username.trim(),
          remoteDir: form.remoteDir.trim(),
          protocol: form.protocol,
          allowInsecure: form.allowInsecure,
          hostKeySha256: form.hostKeySha256.trim(),
          // Only send the password when the admin typed a new one.
          ...(form.password ? { password: form.password } : {}),
        };
        await updateDeployServer(editing, patch);
      }
      closeForm();
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: AdminDeployServer) => {
    if (!confirm(`Delete deploy server “${s.label}” (${s.id})? This cannot be undone.`)) return;
    setError(null);
    try {
      await deleteDeployServer(s.id);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const isNew = editing === "new";

  return (
    <div className="members members-screen">
      <div className="members-head">
        <div className="members-head-inner">
          <div className="members-title-row">
            <div className="members-title">
              <h2>Deploy settings</h2>
              <p>FTP/SFTP servers a plugin can be deployed to. Credentials are stored encrypted in the workspace database.</p>
            </div>
            <div className="members-head-actions">
              <button className="btn btn-ghost" onClick={onClose}>
                <IconBack /> Back to chat
              </button>
              <button className="btn btn-accent" onClick={openNew} disabled={editing !== null}>
                <IconPlus /> Add server
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="members-body">
        <div className="members-body-inner">
          {error && (
            <div className="members-alert" role="alert">
              <span>{error}</span>
              <button onClick={() => setError(null)} aria-label="Dismiss"><IconX /></button>
            </div>
          )}

          {editing !== null && (
            <div className="dsrv-form">
              <h3>{isNew ? "Add deploy server" : `Edit ${editing}`}</h3>
              <div className="dsrv-grid">
                <div className="dsrv-field">
                  <label htmlFor="dsrv-id">Server id</label>
                  <input
                    id="dsrv-id"
                    value={form.id}
                    onChange={(e) => set("id", e.target.value)}
                    placeholder="survivaltest"
                    disabled={!isNew}
                  />
                </div>
                <div className="dsrv-field">
                  <label htmlFor="dsrv-label">Label</label>
                  <input id="dsrv-label" value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="Test Survival" />
                </div>
                <div className="dsrv-field">
                  <label htmlFor="dsrv-host">Host</label>
                  <input id="dsrv-host" value={form.host} onChange={(e) => set("host", e.target.value)} placeholder="ftp.hostify.cz" />
                </div>
                <div className="dsrv-field">
                  <label htmlFor="dsrv-port">Port</label>
                  <input id="dsrv-port" value={form.port} onChange={(e) => set("port", e.target.value)} inputMode="numeric" placeholder="2022" />
                </div>
                <div className="dsrv-field">
                  <label htmlFor="dsrv-user">Username</label>
                  <input id="dsrv-user" value={form.username} onChange={(e) => set("username", e.target.value)} autoComplete="off" />
                </div>
                <div className="dsrv-field">
                  <label htmlFor="dsrv-pass">Password</label>
                  <input
                    id="dsrv-pass"
                    type="password"
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    placeholder={isNew ? "" : "•••• leave blank to keep current"}
                    autoComplete="new-password"
                  />
                </div>
                <div className="dsrv-field">
                  <label htmlFor="dsrv-dir">Remote directory</label>
                  <input id="dsrv-dir" value={form.remoteDir} onChange={(e) => set("remoteDir", e.target.value)} placeholder="plugins" />
                </div>
                <div className="dsrv-field">
                  <label htmlFor="dsrv-proto">Protocol</label>
                  <select id="dsrv-proto" value={form.protocol} onChange={(e) => set("protocol", e.target.value as FormState["protocol"])}>
                    <option value="sftp">SFTP</option>
                    <option value="ftp">FTP (plain)</option>
                    <option value="ftps">FTPS (TLS)</option>
                  </select>
                </div>
                <div className="dsrv-field full">
                  <label htmlFor="dsrv-hostkey">Host key SHA-256 (optional, pins SSH host key)</label>
                  <input id="dsrv-hostkey" value={form.hostKeySha256} onChange={(e) => set("hostKeySha256", e.target.value)} placeholder="base64 sha-256" />
                </div>
                <div className="dsrv-field full">
                  <label className="dsrv-check">
                    <input type="checkbox" checked={form.allowInsecure} onChange={(e) => set("allowInsecure", e.target.checked)} />
                    Skip host-key / TLS verification (test servers only — never for production)
                  </label>
                </div>
              </div>
              <div className="dsrv-form-actions">
                <button className="btn btn-accent" onClick={() => void save()} disabled={saving}>
                  {saving ? "Saving…" : isNew ? "Create server" : "Save changes"}
                </button>
                <button className="btn btn-ghost" onClick={closeForm} disabled={saving}>Cancel</button>
              </div>
              <p className="dsrv-hint">
                Credentials are encrypted at rest with <code>DEPLOY_SECRET_KEY</code>. If that key isn't configured on the
                daemon, saving a password fails.
              </p>
            </div>
          )}

          {servers === null ? (
            <div className="dsrv-empty">Loading servers…</div>
          ) : servers.length === 0 ? (
            <div className="dsrv-empty">No deploy servers yet. Click “Add server” to configure one.</div>
          ) : (
            <div className="dsrv-list">
              {servers.map((s) => (
                <div className="dsrv-row" key={s.id}>
                  <div className="dsrv-main">
                    <h3>{s.label} <span className="dsrv-badge">{s.protocol}</span>{s.allowInsecure && <span className="dsrv-badge insecure">insecure</span>}</h3>
                    <div className="dsrv-meta">{s.username}@{s.host}:{s.port}/{s.remoteDir} · id <code>{s.id}</code></div>
                  </div>
                  <div className="dsrv-actions">
                    <button className="btn btn-ghost" onClick={() => openEdit(s)} disabled={editing !== null}>Edit</button>
                    <button className="btn btn-danger" onClick={() => void remove(s)} disabled={editing !== null}>
                      <IconTrash style={{ width: 13, height: 13 }} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
